import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Check, HeartHandshake, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/live-sessions-survey")({
  head: () => ({
    meta: [
      { title: "Live Sessions — your preferred days and times | Recovery Trauma™" },
      {
        name: "description",
        content:
          "Tell us which days and times work best for live yoga, grounding and workshop sessions with Recovery Trauma™. Takes under a minute.",
      },
      {
        property: "og:title",
        content: "Live Sessions — your preferred days and times | Recovery Trauma™",
      },
      {
        property: "og:description",
        content:
          "Choose the days and times that suit you for live trauma-informed sessions. Under a minute, and you can pick more than one.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: LiveSessionSurveyPage,
});

const DAYS = [
  { value: "sunday", label: "Sunday" },
  { value: "monday", label: "Monday" },
  { value: "other_weekday", label: "Another weekday" },
  { value: "saturday", label: "Saturday" },
  { value: "no_preference", label: "It varies / no preference" },
] as const;

const TIMES = [
  { value: "10:00", label: "Morning — 10:00 AM UK" },
  { value: "13:00", label: "Early afternoon — 1:00 PM UK" },
  { value: "16:00", label: "Late afternoon — 4:00 PM UK" },
  { value: "18:00", label: "Evening — 6:00 PM UK" },
  { value: "19:00", label: "Later evening — 7:00 PM UK" },
  { value: "other", label: "Other" },
] as const;

const YOGA = [
  { value: "yes", label: "Yes, definitely" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "Not really" },
] as const;

function Chip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className="rt-choice flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-start text-[15px] leading-snug"
    >
      <span
        aria-hidden
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
          selected
            ? "border-[var(--gold)] bg-[var(--gold)] text-background"
            : "border-border bg-background"
        }`}
      >
        {selected && <Check className="h-3.5 w-3.5" />}
      </span>
      {children}
    </button>
  );
}

function LiveSessionSurveyPage() {
  const [days, setDays] = useState<string[]>([]);
  const [times, setTimes] = useState<string[]>([]);
  const [yoga, setYoga] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const token = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("t");
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setLoading(false);
        setError(
          "This link is missing its personal code. Please open the link from your email again.",
        );
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const preDay = params.get("day");
      const preTime = params.get("time");
      const preYoga = params.get("yoga");
      const { data } = await supabase.rpc("get_live_session_survey", { _token: token });
      if (cancelled) return;
      const row = (data ?? null) as
        | { days?: string[]; times?: string[]; yoga_interest?: string | null; suggestion?: string | null }
        | null;
      if (!row) {
        setError(
          "We could not find this invitation. Please open the link from your email again, or reply to us and we will resend it.",
        );
        setLoading(false);
        return;
      }
      const startDays = new Set<string>(row.days ?? []);
      if (preDay && DAYS.some((d) => d.value === preDay)) startDays.add(preDay);
      const startTimes = new Set<string>(row.times ?? []);
      if (preTime && TIMES.some((t) => t.value === preTime)) startTimes.add(preTime);
      setDays([...startDays]);
      setTimes([...startTimes]);
      setYoga(
        preYoga && YOGA.some((y) => y.value === preYoga)
          ? preYoga
          : (row.yoga_interest ?? null),
      );
      setSuggestion(row.suggestion ?? "");
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  function toggle(list: string[], set: (v: string[]) => void, value: string) {
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  async function submit() {
    if (!token) return;
    setSaving(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("submit_live_session_survey", {
      _token: token,
      _days: days,
      _times: times,
      _yoga: yoga ?? "",
      _suggestion: suggestion,
    });
    setSaving(false);
    const ok = (data as { ok?: boolean } | null)?.ok;
    if (rpcError || !ok) {
      setError("Something went wrong saving your answers. Please try once more.");
      return;
    }
    setDone(true);
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <div className="rounded-3xl border border-[var(--gold)]/40 bg-card p-8 shadow-sm">
          <HeartHandshake className="mx-auto mb-4 h-8 w-8 text-[var(--gold-deep)]" />
          <h1 className="font-serif text-3xl text-foreground">Thank you 💛</h1>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Your answers are saved. They go straight into our scheduling for the
            live sessions — yoga, grounding practices and workshops. There is
            nothing else to do.
          </p>
          <button
            type="button"
            onClick={() => setDone(false)}
            className="mt-6 text-sm text-muted-foreground underline"
          >
            Change my answers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <HeartHandshake className="h-3.5 w-3.5" /> Under a minute
        </div>
        <h1 className="font-serif text-3xl leading-tight tracking-tight text-foreground md:text-4xl">
          Which days and times suit you for live sessions?
        </h1>
        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-muted-foreground">
          We are planning more live yoga, grounding and nervous-system practices
          and workshops. You can choose as many options as work for you — all
          times are UK time.
        </p>
      </header>

      {error && (
        <div className="mb-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground">
          {error}
        </div>
      )}

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Which day would work best for you?
        </h2>
        <div className="grid gap-2">
          {DAYS.map((d) => (
            <Chip
              key={d.value}
              selected={days.includes(d.value)}
              onClick={() => toggle(days, setDays, d.value)}
            >
              {d.label}
            </Chip>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          What time works best for you? <span className="normal-case">(UK time)</span>
        </h2>
        <div className="grid gap-2">
          {TIMES.map((t) => (
            <Chip
              key={t.value}
              selected={times.includes(t.value)}
              onClick={() => toggle(times, setTimes, t.value)}
            >
              {t.label}
            </Chip>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Would you be interested in live yoga / gentle trauma-informed movement?
        </h2>
        <div className="grid gap-2">
          {YOGA.map((y) => (
            <Chip
              key={y.value}
              selected={yoga === y.value}
              onClick={() => setYoga(yoga === y.value ? null : y.value)}
            >
              {y.label}
            </Chip>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Anything you would particularly like us to offer? (optional)
        </h2>
        <textarea
          value={suggestion}
          onChange={(e) => setSuggestion(e.target.value.slice(0, 600))}
          rows={3}
          placeholder="For example: gentle morning yoga, a boundaries workshop, a quiet co-regulation hour…"
          className="w-full rounded-2xl border border-border bg-card px-4 py-3 text-[15px] text-foreground outline-none focus:border-[var(--gold)]"
        />
      </section>

      <button
        type="button"
        onClick={submit}
        disabled={saving || !token}
        className="w-full rounded-full bg-[var(--gold-deep)] px-6 py-4 text-base font-semibold text-background shadow-sm transition hover:opacity-90 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Send my answers"}
      </button>
      <p className="mt-4 text-center text-xs leading-relaxed text-muted-foreground">
        Your answers are only used to schedule live sessions. There is no
        pressure to attend anything you sign up to hear about.
      </p>
    </div>
  );
}
